import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { queryClient } from 'index';
import i18n from 'providers/i18n/i18n';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import {
  addToShoppingCart,
  editShoppingCartItem,
  getShoppingCart,
} from 'services/ShoppingCartService';
import { LogyxError, getToastErrorMessage } from 'services/api/errors';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import { setCartLength } from 'store/ShoppingCart/actions/shoppingCart';
import { AppMode } from 'store/Common/reducers/common';

export const useGetShoppingCart = (
  appMode: AppMode,
  cartId?: string | undefined
) => {
  const dispatch = useDispatch();

  const queryResult = useQuery({
    queryKey: [ReactQueryKeys.GET_SHOPPING_CART, i18n.resolvedLanguage, cartId],
    queryFn: () => {
      return getShoppingCart(appMode, cartId);
    },
    enabled: !!cartId,
  });

  // Dispatch cartLength to Redux when data is available. Orders return `lines`
  // as an array; quotations return it grouped by supplier
  // (Record<supplierName, line[]>), so sum the group lengths in that case.
  useEffect(() => {
    const lines = queryResult.data?.lines;
    if (lines) {
      const cartLength = Array.isArray(lines)
        ? lines.length
        : Object.values(lines).reduce(
            (total: number, group: any) => total + (group?.length ?? 0),
            0
          );
      dispatch(setCartLength(cartLength));
    }
  }, [queryResult.data, dispatch]);

  return queryResult;
};

interface IAddToShoppingCartParams {
  cartLineQuantity: number;
  modelId: number;
  companyId: number;
  lang: string;
  queryPayload: any;
  pricing: any;
  shoppingCartId?: string | undefined;
}

export const useAddToShoppingCart = () =>
  useMutation(
    (params: IAddToShoppingCartParams) => {
      return addToShoppingCart(
        params.cartLineQuantity,
        params.modelId,
        params.companyId,
        params.lang,
        params.queryPayload,
        params.pricing,
        params.shoppingCartId
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ReactQueryKeys.GET_SHOPPING_CART]);
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.ADD_TO_SHOPPING_CART,
    }
  );

interface IEditShoppingCartItem {
  cartLineId: number;
  cartLineQuantity: number;
  modelId: number;
  companyId: number;
  lang: string;
  queryPayload: any;
  pricing: any;
  shoppingCartId?: string | undefined;
}

export const useEditShoppingCartItem = () => {
  return useMutation(
    (params: IEditShoppingCartItem) => {
      return editShoppingCartItem(
        params.cartLineId,
        params.cartLineQuantity,
        params.modelId,
        params.companyId,
        params.lang,
        params.queryPayload,
        params.pricing,
        params.shoppingCartId
      );
    },
    {
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      onSettled: () => {
        queryClient.removeQueries(ReactQueryKeys.GET_SHOPPING_CART);
      },
      mutationKey: ReactMutationKeys.EDIT_SHOPPING_CART_ITEM,
    }
  );
};
